import { useEffect, useRef, useState, type FormEvent } from "react";
import { Link, useParams } from "react-router";
import { api, authenticatedFetch } from "../api";
import { ErrorState, Loading } from "../ui/State";
import type { Enquiry } from "../enquiries";
import { formatDateTime } from "../dates";
import "./EnquiryProposal.css";

type Options = {
  residentialAnnualOverridePence: number | null;
  commercialAnnualPence: number | null;
  onboarding: "Quote separately" | "Waived" | "Fixed";
  onboardingPence: number | null;
  priorities: string;
  handover: string;
  feeNote: string;
};
type Data = {
  facts: Enquiry;
  photo: {
    documentId: string;
    versionId: string;
    title: string;
    credit: string;
  } | null;
  options: Options;
  fees: {
    ratePence: number | null;
    annualPence: number | null;
    warnings: string[];
  };
  templateRevision: string;
  latestProposalDocumentId: string | null;
  canPrepare: boolean;
  canUploadPhoto: boolean;
  history: {
    documentId: string;
    title: string;
    createdAt: string;
    number: number;
    status: string;
    warnings: string[];
  }[];
};
const money = (p: number | null) =>
  p === null
    ? "To be confirmed"
    : new Intl.NumberFormat("en-GB", {
        style: "currency",
        currency: "GBP",
      }).format(p / 100);
const pounds = (p: number | null) => (p === null ? "" : String(p / 100));
const pence = (form: FormData, name: string) => {
  const s = String(form.get(name) ?? "").trim();
  return s === "" ? null : Math.round(Number(s) * 100);
};
export function EnquiryProposal() {
  const { id } = useParams(),
    [data, setData] = useState<Data>(),
    [error, setError] = useState<unknown>(),
    [busy, setBusy] = useState(false),
    [saved, setSaved] = useState<string>(),
    [photoUrl, setPhotoUrl] = useState("");
  const key = useRef<string | undefined>(undefined),
    inFlight = useRef(false),
    [onboarding, setOnboarding] =
      useState<Options["onboarding"]>("Quote separately");
  const activeId = useRef(id);
  async function load() {
    const next = await api<Data>(`/api/enquiries/${id}/proposal`);
    if (activeId.current !== id) return;
    setData(next);
    setOnboarding(next.options.onboarding);
  }
  useEffect(() => {
    activeId.current = id;
    setData(undefined);
    setSaved(undefined);
    setError(undefined);
    key.current = undefined;
    let active = true;
    void api<Data>(`/api/enquiries/${id}/proposal`)
      .then((next) => {
        if (active) {
          setData(next);
          setOnboarding(next.options.onboarding);
        }
      })
      .catch((e) => {
        if (active) setError(e);
      });
    return () => {
      active = false;
      activeId.current = undefined;
    };
  }, [id]);
  useEffect(() => {
    setPhotoUrl("");
    const p = data?.photo;
    if (!p) return;
    const controller = new AbortController();
    let url = "";
    void authenticatedFetch(
      `/api/documents/${p.documentId}/versions/${p.versionId}/content`,
      { signal: controller.signal },
    )
      .then(async (r) => {
        if (!r.ok) throw Error("The cover photo could not be loaded.");
        const b = await r.blob();
        if (!b.type.startsWith("image/"))
          throw Error("The cover photo cannot be displayed.");
        if (!controller.signal.aborted) {
          url = URL.createObjectURL(b);
          setPhotoUrl(url);
        }
      })
      .catch((e) => {
        if (!controller.signal.aborted) setError(e);
      });
    return () => {
      controller.abort();
      if (url) URL.revokeObjectURL(url);
    };
  }, [data?.photo?.versionId]);
  async function prepare(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!data || inFlight.current) return;
    inFlight.current = true;
    setBusy(true);
    setError(undefined);
    setSaved(undefined);
    const form = new FormData(event.currentTarget),
      options: Options = {
        residentialAnnualOverridePence: pence(form, "residentialOverride"),
        commercialAnnualPence: pence(form, "commercialAnnual"),
        onboarding,
        onboardingPence:
          onboarding === "Fixed" ? pence(form, "onboardingFee") : null,
        priorities: String(form.get("priorities") ?? ""),
        handover: String(form.get("handover") ?? ""),
        feeNote: String(form.get("feeNote") ?? ""),
      };
    key.current ??= crypto.randomUUID();
    try {
      const result = await api<{ documentId: string }>(
        `/api/enquiries/${id}/proposals`,
        {
          method: "POST",
          body: JSON.stringify({
            expectedVersion: data.facts.version,
            expectedPhotoVersion: data.photo?.versionId ?? null,
            latestProposalDocumentId: data.latestProposalDocumentId,
            idempotencyKey: key.current,
            options,
          }),
        },
      );
      if (activeId.current !== id) return;
      key.current = undefined;
      setSaved(result.documentId);
      await load();
    } catch (e) {
      if (activeId.current === id) setError(e);
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  }
  async function upload(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!data || inFlight.current) return;
    const form = new FormData(event.currentTarget),
      file = form.get("photo");
    if (!(file instanceof File) || !file.size) return;
    if (file.size > 10 * 1024 * 1024) {
      setError(Error("Choose a photograph up to 10 MB."));
      return;
    }
    inFlight.current = true;
    setBusy(true);
    setError(undefined);
    try {
      const bytes = await file.arrayBuffer(),
        checksum = Array.from(
          new Uint8Array(await crypto.subtle.digest("SHA-256", bytes)),
        )
          .map((v) => v.toString(16).padStart(2, "0"))
          .join("");
      const query = new URLSearchParams({
        fileName: file.name,
        credit: String(form.get("credit") ?? ""),
        expectedVersion: String(data.facts.version),
        checksum,
      });
      await api(`/api/enquiries/${id}/proposal/photo?${query}`, {
        method: "PUT",
        headers: { "content-type": "application/octet-stream" },
        body: bytes,
      });
      if (activeId.current !== id) return;
      key.current = undefined;
      await load();
    } catch (e) {
      if (activeId.current === id) setError(e);
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  }
  if (!data) return error ? <ErrorState error={error} /> : <Loading />;
  const f = data.facts,
    o = data.options;
  return (
    <div className="record-column">
      <header className="page-heading">
        <div>
          <h1>{f.building_name ?? "Building"} proposal</h1>
          <p>{f.enquiry_number} · BlockGenie · Prepared for review</p>
        </div>
        <Link to={`/enquiries/${id}`}>Back to lead</Link>
      </header>
      {error !== undefined && <ErrorState error={error} />}
      {saved && (
        <p className="success-panel" role="status">
          Proposal saved for review.{" "}
          <Link to={`/documents/${saved}`}>Open the proposal PDF</Link>
        </p>
      )}
      <section className="panel">
        <h2>Building details</h2>
        <p>
          {[
            f.address_line_1,
            f.address_line_2,
            f.town_city,
            f.county,
            f.postcode,
          ]
            .filter(Boolean)
            .join(", ") || "Address not recorded"}
        </p>
        <p>
          {f.residential_units ?? "Unknown"} residential flats ·{" "}
          {f.commercial_units ?? "Unknown"} commercial units ·{" "}
          {f.hrb_status ?? "Building safety classification unknown"}
        </p>
        <p>
          Prepared for{" "}
          {f.prospective_client_name ??
            f.primary_contact_name ??
            "client to be confirmed"}
          .
        </p>
        <Link to={`/enquiries/${id}#enquiry-facts`}>
          Edit building facts in the lead
        </Link>
      </section>
      <section className="panel">
        <h2>Cover photo</h2>
        {photoUrl ? (
          <img
            className="building-photo__preview"
            src={photoUrl}
            alt={`Cover photograph of ${f.building_name ?? "the building"}`}
          />
        ) : (
          <p>
            {data.photo
              ? "Loading saved photograph…"
              : "No approved photo is available. A review PDF can still be prepared with a photo placeholder."}
          </p>
        )}
        {data.photo && <p>{data.photo.credit || data.photo.title}</p>}
        {data.canUploadPhoto && (
          <details>
            <summary>
              {data.photo ? "Change cover photo" : "Add cover photo"}
            </summary>
            <form
              className="enquiry-proposal-form"
              onSubmit={(event) => void upload(event)}
            >
              <fieldset disabled={busy}>
                <legend>Building photograph</legend>
                <label>
                  Photo
                  <input
                    name="photo"
                    type="file"
                    accept="image/png,image/jpeg,image/webp"
                    required
                  />
                </label>
                <label>
                  Photo credit / source
                  <input
                    name="credit"
                    maxLength={240}
                    required
                    placeholder="For example: photograph taken by our team"
                  />
                </label>
                <p>
                  Use a photograph you have permission to include in proposals.
                  The original is retained with this enquiry.
                </p>
                <button>Save cover photo</button>
              </fieldset>
            </form>
          </details>
        )}
      </section>
      <section className="panel">
        <h2>Tailor the proposal</h2>
        <p>
          The proposal introduces our services, approach, team, experience and
          BlockGenie OS. Add the priorities and handover arrangements discussed
          with this client.
        </p>
        {data.fees.warnings.length > 0 && (
          <div role="status">
            <h3>Details to confirm</h3>
            <ul>
              {data.fees.warnings.map((w) => (
                <li key={w}>{w}</li>
              ))}
            </ul>
          </div>
        )}
        <form
          className="enquiry-proposal-form"
          key={data.latestProposalDocumentId ?? "new"}
          onChange={() => {
            key.current = undefined;
          }}
          onSubmit={(event) => void prepare(event)}
        >
          <fieldset disabled={busy || !data.canPrepare}>
            <legend>Proposal content and fees</legend>
            <label>
              Building priorities
              <textarea
                name="priorities"
                rows={4}
                maxLength={1500}
                defaultValue={o.priorities}
                placeholder="Specific priorities discussed with this client"
              />
            </label>
            <label>
              Handover approach
              <textarea
                name="handover"
                rows={3}
                maxLength={800}
                defaultValue={o.handover}
                placeholder="Leave blank to use the standard coordinated handover wording"
              />
            </label>
            <p>
              Published rates include VAT: £275 per flat annually, or £350 for a
              confirmed higher-risk building. Prefilled annual total:{" "}
              <strong>{money(data.fees.annualPence)}</strong>. The PDF will
              recalculate the total using any fee changes below. Unknown facts
              remain unquoted.
            </p>
            <label>
              Residential annual fee override (£, including VAT)
              <input
                name="residentialOverride"
                type="number"
                min="0"
                max="999999999.99"
                step="0.01"
                defaultValue={pounds(o.residentialAnnualOverridePence)}
              />
              <small>
                Optional. Leave blank to calculate from the flat count and
                confirmed classification.
              </small>
            </label>
            {f.commercial_units !== 0 && (
              <label>
                Commercial annual fee (£, including VAT)
                <input
                  name="commercialAnnual"
                  type="number"
                  min="0"
                  max="999999999.99"
                  step="0.01"
                  defaultValue={pounds(o.commercialAnnualPence)}
                />
              </label>
            )}
            <label>
              Onboarding
              <select
                value={onboarding}
                onChange={(e) =>
                  setOnboarding(e.target.value as Options["onboarding"])
                }
              >
                <option>Quote separately</option>
                <option>Waived</option>
                <option>Fixed</option>
              </select>
            </label>
            {onboarding === "Fixed" && (
              <label>
                One-off onboarding fee (£, including VAT)
                <input
                  name="onboardingFee"
                  type="number"
                  min="0"
                  max="999999999.99"
                  step="0.01"
                  required
                  defaultValue={pounds(o.onboardingPence)}
                />
              </label>
            )}
            <label>
              Fee explanation
              <textarea
                name="feeNote"
                rows={2}
                maxLength={500}
                defaultValue={o.feeNote}
                placeholder="Required for a residential fee override"
              />
            </label>
            <p>
              Prepare a PDF for review. It will be saved in this enquiry’s
              documents. This does not send it, approve it or change the lead
              stage.
            </p>
            <button>{busy ? "Preparing…" : "Prepare proposal PDF"}</button>
          </fieldset>
        </form>
        {!data.canPrepare && (
          <p>
            Proposal preparation requires enquiry and document management
            access, with document storage available.
          </p>
        )}
      </section>
      <section className="panel">
        <h2>Proposal history</h2>
        {data.history.length ? (
          <ul>
            {data.history.map((v) => (
              <li key={v.documentId}>
                <Link to={`/documents/${v.documentId}`}>{v.title}</Link> ·{" "}
                {formatDateTime(v.createdAt)} · {v.status}
                {v.warnings.length > 0 && (
                  <span> · {v.warnings.length} items to confirm</span>
                )}
              </li>
            ))}
          </ul>
        ) : (
          <p>No proposal has been prepared yet.</p>
        )}
        <small>
          Earlier PDFs retain the facts, photo and fees used when they were
          prepared.
        </small>
      </section>
    </div>
  );
}
